/**
 * LocalStorage Cleanup Utility
 * Clears localStorage after 10 minutes of app being closed/inactive
 * Only tracks when the app is closed (window hidden/closed), not while app is open
 */

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const LAST_ACTIVE_KEY = "app_last_active_time";
const CLEANUP_CHECK_KEY = "app_cleanup_check";

/**
 * Initialize the cleanup mechanism
 * Call this on app startup
 */
export const initLocalStorageCleanup = () => {
  // Check if we need to cleanup on app startup
  checkAndCleanupOnStartup();

  // Set up visibility change listener to track when app is closed
  setupVisibilityListener();

  // Update last active time on app load
  updateLastActiveTime();
};

/**
 * Check if localStorage should be cleared on app startup
 */
const checkAndCleanupOnStartup = () => {
  try {
    const lastActiveTime = localStorage.getItem(LAST_ACTIVE_KEY);
    const cleanupScheduled = localStorage.getItem(CLEANUP_CHECK_KEY);

    if (!lastActiveTime || !cleanupScheduled) {
      // First time running or no cleanup scheduled
      updateLastActiveTime();
      return;
    }

    const lastActive = parseInt(lastActiveTime, 10);
    const now = Date.now();
    const timeSinceLastActive = now - lastActive;

    console.log("📊 LocalStorage Cleanup Check:", {
      lastActive: new Date(lastActive).toLocaleString(),
      timeSinceLastActive: Math.floor(timeSinceLastActive / 1000 / 60) + " minutes",
      threshold: "10 minutes",
    });

    // If more than 10 minutes have passed since app was closed
    if (timeSinceLastActive >= INACTIVITY_TIMEOUT) {
      console.log("🧹 Clearing localStorage due to inactivity...");
      
      // Clear everything except some critical keys if needed
      const keysToPreserve = [
        // Add any keys you want to preserve here
        // Example: "user_preferences", "theme"
      ];

      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (!keysToPreserve.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      console.log("✅ LocalStorage cleared successfully");
    } else {
      const remainingTime = INACTIVITY_TIMEOUT - timeSinceLastActive;
      console.log(
        `⏳ ${Math.floor(remainingTime / 1000 / 60)} minutes remaining until cleanup`
      );
    }

    // Update last active time since app is now open
    updateLastActiveTime();
  } catch (error) {
    console.error("❌ Error in localStorage cleanup:", error);
  }
};

/**
 * Set up visibility change listener
 * Track when user closes/hides the app
 */
const setupVisibilityListener = () => {
  // Track when the page becomes hidden (app closed/minimized)
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      // App is now hidden/closed
      console.log("👋 App closed/hidden - marking timestamp for cleanup");
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      localStorage.setItem(CLEANUP_CHECK_KEY, "true");
    } else {
      // App is now visible again
      console.log("👀 App opened - resetting cleanup timer");
      updateLastActiveTime();
    }
  });

  // Also track beforeunload (when browser/tab is closing)
  window.addEventListener("beforeunload", () => {
    console.log("🚪 App closing - marking timestamp for cleanup");
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    localStorage.setItem(CLEANUP_CHECK_KEY, "true");
  });
};

/**
 * Update the last active timestamp
 * Called when app is actively being used
 */
const updateLastActiveTime = () => {
  localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
  localStorage.removeItem(CLEANUP_CHECK_KEY);
};

/**
 * Manually clear localStorage
 * Can be called programmatically if needed
 */
export const manualClearLocalStorage = () => {
  localStorage.clear();
  console.log("🧹 LocalStorage manually cleared");
};

/**
 * Get time since last active
 * Useful for debugging
 */
export const getTimeSinceLastActive = () => {
  const lastActiveTime = localStorage.getItem(LAST_ACTIVE_KEY);
  if (!lastActiveTime) return null;

  const timeSince = Date.now() - parseInt(lastActiveTime, 10);
  return {
    milliseconds: timeSince,
    seconds: Math.floor(timeSince / 1000),
    minutes: Math.floor(timeSince / 1000 / 60),
  };
};
